import { useSearchParams } from 'react-router';
import PageShell from '../../../components/PageShell';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useRedirect } from '../../../optumus/optumus-chat/src/hooks';

/**
 * Error route reachable from any failure path.
 * Maps a closed list of error codes to member-facing copy.
 */
export default function ErrorRoute() {
  const [params] = useSearchParams();
  const { handleClose } = useRedirect();
  const code = params.get('code') ?? 'UNKNOWN';
  const view = viewForCode(code);

  return (
    <PageShell>
      <Flex
        direction="column"
        alignItems="center"
        justify="center"
        css={{ minHeight: '100vh', textAlign: 'center', padding: '$core.spacing.400' }}
      >
        <IconSymbol icon="report" variant="filled" size={80} color="#F59E0B" />

        <Heading
          level={1}
          css={{
            fontSize: '$core.font-size.h1.100',
            fontWeight: '$core.font-weight.bold',
            color: '$core.color.neutral.100',
          }}
        >
          {view.title}
        </Heading>
        <Text
          css={{
            marginTop: '$core.spacing.200',
            color: '$core.color.neutral.70',
            lineHeight: 1.6,
            maxWidth: '360px',
            fontSize: '$core.font-size.p.80',
          }}
        >
          {view.body}
        </Text>
        <Flex
          direction="column"
          css={{
            marginTop: '$core.spacing.500',
            gap: '$core.spacing.150',
            width: '100%',
            maxWidth: '320px',
          }}
        >
          <Button
            variant="filled"
            onClick={handleClose}
            css={{
              minHeight: '48px',
              fontWeight: '$core.font-weight.bold',
              backgroundColor: '$core.color.brand.100',
              color: '$core.color.neutral.0',
              borderRadius: '$core.border-radius.md',
              '&:hover': { backgroundColor: '$core.color.brand.80' },
            }}
          >
            Return to App
          </Button>
        </Flex>
      </Flex>
    </PageShell>
  );
}

interface ErrorView {
  title: string;
  body: string;
}

const VIEWS: Record<string, ErrorView> = {
  NOT_FOUND: {
    title: 'Page Not Found',
    body: 'We could not find the page you are looking for. Please return to the app.',
  },
  CONSENT_CHECK_FAILED: {
    title: 'Unable to Verify Consent',
    body: 'We encountered an issue checking your consent status. Please return to the app.',
  },
};

function viewForCode(code: string): ErrorView {
  return (
    VIEWS[code] ?? {
      title: 'Something Went Wrong',
      body: 'An unexpected error occurred. ',
    }
  );
}
